/**
 * Video Selector Modal Component
 * Modal for selecting videos from the CenterStage library or by pasting an external URL.
 * Follows the same architectural pattern as ImageInsertModal.
 *
 * Split per STR-5: styles in videoSelectorModalStyles.ts, URL-preview flow in
 * hooks/useVideoUrlPreview.ts, tab panels in VideoSelectorLibraryTab /
 * VideoSelectorExternalUrlTab. State and confirm handlers live here.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  useImageSourceWithFallback,
  useTheme,
  type VideoTrack,
} from '@course-authoring/shared-types';
import {
  searchVideos,
  getVideo,
  type CenterstageVideo,
} from '../api/centerstageApi';
import { useVideoUrlPreview } from '../hooks/useVideoUrlPreview';
import { formatDuration } from '../utils/formatDuration';
import { ModalShell } from './ModalShell';
import { VideoSelectorLibraryTab } from './VideoSelectorLibraryTab';
import { VideoSelectorExternalUrlTab } from './VideoSelectorExternalUrlTab';
import { createVideoSelectorModalStyles } from './videoSelectorModalStyles';

// Re-exported for existing consumers (VideoBlockEditor) that import it from here.
export { formatDuration } from '../utils/formatDuration';

// ── Public types ──────────────────────────────────────────────────────────────

export interface VideoSelectionResult {
  url: string;
  posterUrl?: string;
  tracks: VideoTrack[];
  centerstageVideoId?: string;
  title?: string;
}

export interface CurrentVideoData {
  url: string;
  posterUrl?: string;
  centerstageVideoId?: string;
  title?: string;
}

interface VideoSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (data: VideoSelectionResult) => void;
  onRemove: () => void;
  currentVideo?: CurrentVideoData;
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

type TabId = 'library' | 'external-url';

// ── Component ─────────────────────────────────────────────────────────────────

export function VideoSelectorModal({
  isOpen,
  onClose,
  onSelect,
  onRemove,
  currentVideo,
}: VideoSelectorModalProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createVideoSelectorModalStyles(tokens), [tokens]);

  // ── Tab state ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabId>('library');

  // ── Library state ─────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CenterstageVideo[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedLibraryVideo, setSelectedLibraryVideo] = useState<CenterstageVideo | null>(null);
  const [hasExplicitlyDeselected, setHasExplicitlyDeselected] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ── External URL state ────────────────────────────────────────────────────
  const {
    externalUrl,
    externalUrlError,
    urlPreview,
    isPreviewLoading,
    handleUrlChange,
    handlePreview,
    setExternalUrlError,
    reset: resetUrlPreview,
  } = useVideoUrlPreview();

  // ── Current video duration (fetched from CenterStage) ──────────────────
  const [currentVideoDuration, setCurrentVideoDuration] = useState<number | undefined>(undefined);

  // ── Derived ───────────────────────────────────────────────────────────────
  const hasCurrentVideo = Boolean(currentVideo?.url);
  const {
    resolvedSrc: currentVideoPosterUrl,
    handleError: handleCurrentVideoPosterError,
  } = useImageSourceWithFallback(currentVideo?.posterUrl);

  // ── Reset on close ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('library');
      setQuery('');
      setResults([]);
      setIsSearching(false);
      setSearchError(null);
      setHasSearched(false);
      setSelectedLibraryVideo(null);
      setHasExplicitlyDeselected(false);
      setIsLoadingDetails(false);
      resetUrlPreview();
      setCurrentVideoDuration(undefined);
    }
  }, [isOpen, resetUrlPreview]);

  // Fetch current video duration from CenterStage when modal opens
  useEffect(() => {
    if (!isOpen || !currentVideo?.centerstageVideoId) return;
    let cancelled = false;
    getVideo(currentVideo.centerstageVideoId).then((details) => {
      if (!cancelled && details?.duration !== undefined) {
        setCurrentVideoDuration(details.duration);
      }
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [isOpen, currentVideo?.centerstageVideoId]);

  // ── Debounced search ──────────────────────────────────────────────────────
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setResults([]);
      setHasSearched(false);
      setSearchError(null);
      return;
    }

    let cancelled = false;
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      setSearchError(null);
      try {
        const data = await searchVideos(query);
        if (cancelled) return;
        setResults(data.videos);
        setHasSearched(true);
      } catch (err) {
        if (cancelled) return;
        setSearchError(err instanceof Error ? err.message : 'Search failed');
        setResults([]);
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query]);

  // ── Library handlers ──────────────────────────────────────────────────────
  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    setSelectedLibraryVideo(null);
    setHasExplicitlyDeselected(false);
  }, []);

  const handleToggleLibraryVideo = useCallback((video: CenterstageVideo, isSelected: boolean) => {
    if (isSelected) {
      setSelectedLibraryVideo(null);
      setHasExplicitlyDeselected(true);
    } else {
      setSelectedLibraryVideo(video);
      setHasExplicitlyDeselected(false);
    }
  }, []);

  // ── Library confirm ───────────────────────────────────────────────────────
  const handleLibraryConfirm = useCallback(async () => {
    if (!selectedLibraryVideo) return;

    setIsLoadingDetails(true);
    setSearchError(null);

    try {
      const videoDetails = await getVideo(selectedLibraryVideo.id);

      const tracks: VideoTrack[] = (videoDetails?.captions || []).map((caption) => ({
        kind: caption.kind as 'captions' | 'subtitles' | 'chapters',
        src: caption.file,
        label: caption.language,
        language: caption.languageCode,
      }));

      onSelect({
        centerstageVideoId: selectedLibraryVideo.id,
        url: videoDetails?.url || selectedLibraryVideo.url,
        posterUrl: videoDetails?.posterUrl || selectedLibraryVideo.posterUrl || selectedLibraryVideo.thumbnailUrl,
        tracks,
        title: selectedLibraryVideo.title,
      });
    } catch {
      onSelect({
        centerstageVideoId: selectedLibraryVideo.id,
        url: selectedLibraryVideo.url,
        posterUrl: selectedLibraryVideo.posterUrl || selectedLibraryVideo.thumbnailUrl,
        tracks: [],
        title: selectedLibraryVideo.title,
      });
    } finally {
      setIsLoadingDetails(false);
    }
  }, [selectedLibraryVideo, onSelect]);

  // ── External URL confirm ──────────────────────────────────────────────────
  const handleExternalUrlConfirm = useCallback(() => {
    const trimmed = externalUrl.trim();
    if (!trimmed) {
      setExternalUrlError('Please enter a video URL.');
      return;
    }

    try {
      new URL(trimmed);
    } catch {
      setExternalUrlError('Please enter a valid URL.');
      return;
    }

    onSelect({
      url: trimmed,
      tracks: [],
      title: urlPreview?.title,
      posterUrl: urlPreview?.thumbnailUrl,
    });
  }, [externalUrl, urlPreview, onSelect, setExternalUrlError]);

  // ── Confirm dispatcher ────────────────────────────────────────────────────
  const handleConfirm = useCallback(() => {
    if (activeTab === 'library') {
      handleLibraryConfirm();
    } else {
      handleExternalUrlConfirm();
    }
  }, [activeTab, handleLibraryConfirm, handleExternalUrlConfirm]);

  const canConfirm =
    activeTab === 'library'
      ? Boolean(selectedLibraryVideo) && !isLoadingDetails
      : externalUrl.trim().length > 0;

  const confirmLabel = hasCurrentVideo ? 'Use this video' : 'Select video';

  // ── Render ────────────────────────────────────────────────────────────────

  const tabs: { id: TabId; label: string }[] = [
    { id: 'library', label: 'Library' },
    { id: 'external-url', label: 'External URL' },
  ];

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy="video-modal-title"
      width="720px"
      borderRadius="12px"
    >
        {/* ── Header ────────────────────────────────────────────────── */}
        <div style={styles.header}>
          <div style={styles.headerText}>
            <h3 id="video-modal-title" style={styles.title}>
              {hasCurrentVideo ? 'Change video' : 'Select a video'}
            </h3>
            <p style={styles.subtitle}>
              {hasCurrentVideo
                ? 'Select a replacement from the library or by pasting a URL.'
                : 'Choose from the library or paste a URL.'}
            </p>
          </div>
          <button
            style={styles.closeButton}
            onClick={onClose}
            type="button"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* ── Current Video Card ────────────────────────────────────── */}
        {hasCurrentVideo && currentVideo && (
          <div style={styles.currentVideoCard}>
            <div style={styles.currentVideoThumbnail}>
              {currentVideoPosterUrl ? (
                <img
                  src={currentVideoPosterUrl}
                  alt={currentVideo.title || 'Current video'}
                  style={styles.currentVideoImage}
                  crossOrigin="anonymous"
                  onError={handleCurrentVideoPosterError}
                />
              ) : (
                <div style={styles.currentVideoPlaceholder}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                </div>
              )}
            </div>
            <div style={styles.currentVideoInfo}>
              <span style={styles.currentVideoLabel}>CURRENT VIDEO</span>
              <span style={styles.currentVideoTitle}>
                {currentVideo.title || 'Current video'}
              </span>
              {currentVideo.centerstageVideoId && (
                <span style={styles.currentVideoSource}>
                  CenterStage #{currentVideo.centerstageVideoId}{currentVideoDuration !== undefined ? ` - ${formatDuration(currentVideoDuration)}` : ''}
                </span>
              )}
            </div>
            <button
              type="button"
              style={styles.removeButton}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              Remove
            </button>
          </div>
        )}

        {/* ── Tabs ──────────────────────────────────────────────────── */}
        <div style={styles.tabBar} role="tablist" aria-label="Video source tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`video-tabpanel-${tab.id}`}
              id={`video-tab-${tab.id}`}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab Content ───────────────────────────────────────────── */}
        <div
          id={`video-tabpanel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`video-tab-${activeTab}`}
          style={styles.tabContent}
        >
          {activeTab === 'library' && (
            <VideoSelectorLibraryTab
              query={query}
              onQueryChange={handleQueryChange}
              results={results}
              isSearching={isSearching}
              searchError={searchError}
              hasSearched={hasSearched}
              selectedLibraryVideo={selectedLibraryVideo}
              hasExplicitlyDeselected={hasExplicitlyDeselected}
              currentVideoCenterstageId={currentVideo?.centerstageVideoId}
              onToggleVideo={handleToggleLibraryVideo}
              styles={styles}
            />
          )}

          {activeTab === 'external-url' && (
            <VideoSelectorExternalUrlTab
              externalUrl={externalUrl}
              externalUrlError={externalUrlError}
              urlPreview={urlPreview}
              isPreviewLoading={isPreviewLoading}
              onUrlChange={handleUrlChange}
              onPreview={handlePreview}
              styles={styles}
            />
          )}
        </div>

        {/* ── Footer ────────────────────────────────────────────────── */}
        <div style={styles.footer}>
          <span style={styles.footerHint}>
            {activeTab === 'library'
              ? results.length > 0
                ? `${results.length} video${results.length === 1 ? '' : 's'} in library`
                : 'Search the video library'
              : 'Supports YouTube, Vimeo, MP4'}
          </span>
          <div style={styles.footerActions}>
            <button type="button" style={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...styles.confirmButton,
                opacity: canConfirm ? 1 : 0.5,
                cursor: canConfirm ? 'pointer' : 'not-allowed',
              }}
              disabled={!canConfirm}
              onClick={handleConfirm}
            >
              {isLoadingDetails ? 'Loading…' : confirmLabel}
            </button>
          </div>
        </div>
    </ModalShell>
  );
}
